import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

export default function ChartContainer({ data, type = 'bar' }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Prepare data for different chart types
    if (type === 'bar') {
      // Gender distribution
      const maleCount = data.filter(a => a.gender === 'Male').length;
      const femaleCount = data.filter(a => a.gender === 'Female').length;
      return [
        { name: 'Male', count: maleCount, fill: '#3b82f6' },
        { name: 'Female', count: femaleCount, fill: '#ec4899' }
      ];
    }

    if (type === 'scatter') {
      // Height vs Vertical Jump
      return data.map(a => ({
        height: parseFloat(a.height_cm),
        verticalJump: parseFloat(a.vertical_jump_cm || 0),
        name: `${a.first_name} ${a.last_name}`
      }));
    }

    if (type === 'age') {
      // Age distribution
      const ageGroups = {};
      data.forEach(a => {
        const age = new Date().getFullYear() - new Date(a.date_of_birth).getFullYear();
        ageGroups[age] = (ageGroups[age] || 0) + 1;
      });
      return Object.entries(ageGroups).map(([age, count]) => ({
        age: `${age} yrs`,
        count
      }));
    }

    return [];
  }, [data, type]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-400">No data to visualize</p>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-xl">
          {payload.map((entry, index) => (
            <div key={index} className="text-sm">
              <span className="text-slate-300">{entry.name}: </span>
              <span className="text-white font-semibold">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (type === 'bar') {
    return (
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Gender Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="name" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" radius={[8, 8, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'scatter') {
    return (
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Height vs Vertical Jump</h3>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis 
              dataKey="height" 
              name="Height (cm)" 
              stroke="#94a3b8"
              label={{ value: 'Height (cm)', position: 'insideBottom', offset: -5, fill: '#94a3b8' }}
            />
            <YAxis 
              dataKey="verticalJump" 
              name="Vertical Jump (cm)" 
              stroke="#94a3b8"
              label={{ value: 'Vertical Jump (cm)', angle: -90, position: 'insideLeft', fill: '#94a3b8' }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={chartData} fill="#10b981" />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'age') {
    return (
      <div>
        <h3 className="text-lg font-semibold text-white mb-4">Age Distribution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
            <XAxis dataKey="age" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="count" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return null;
}
